import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Calendar, Users } from 'lucide-react';
import useIsMobile from '../../../hooks/useIsMobile';
import PlaceAutocomplete from './PlaceAutocomplete';
import { toPlaceGeo } from './PlaceSearchService';
import ThemedSelect from './Configure/components/ThemedSelect';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';

const newPerson = () => ({ id: Date.now() + Math.random(), name: '', gender: 'Male', dob: '', tob: '', place: '', placeGeo: null, details: '' });

export default function BirthDateModal({ open, onClose, projectId = null }) {
  const isMobile = useIsMobile();
  const [persons, setPersons] = useState([newPerson()]);
  const personsKey = `vastu_persons_data_${projectId || 'new'}`;
  const numeroKey  = `numero_vastu_data_${projectId || 'new'}`;

  useEffect(() => {
    if (!open) return;
    try {
      // Scoped key first; fall back to the old flat/unscoped keys so data
      // saved before this was project-scoped isn't suddenly invisible.
      const raw = localStorage.getItem(personsKey) || localStorage.getItem(numeroKey)
        || localStorage.getItem('vastu_persons_data') || localStorage.getItem('numero_vastu_data');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) { setPersons(parsed); return; }
      }
    } catch (e) {}
    setPersons([newPerson()]);
  }, [open, personsKey, numeroKey]);

  const handleChange = (id, field, val) =>
    setPersons(prev => prev.map(p => p.id === id ? { ...p, [field]: val } : p));
  const handleAdd = () => setPersons(prev => [...prev, newPerson()]);
  const handleRemove = (id) =>
    setPersons(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);
  const handleSave = () => {
    try {
      localStorage.setItem(personsKey, JSON.stringify(persons));
      localStorage.setItem(numeroKey,  JSON.stringify(persons));
    } catch (e) {}
    onClose();
  };

  const leave = useLeaveConfirm({ onSave: handleSave, onLeave: onClose });

  if (!open) return null;

  const inp = {
    width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0',
    borderRadius: '8px', fontSize: '13px', outline: 'none',
    background: '#fff', color: '#0f172a', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '920px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', background: 'linear-gradient(135deg, #00A19D, #00897B)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={19} color="#fff" />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>Add Birth Dates</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11.5px', marginTop: 2 }}>For Astro & Numero Vastu analysis</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '11px', background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
              <Users size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{persons.length}
            </span>
            <button onClick={leave.requestClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Column headers — a 7-column grid header only makes sense next to
            the 7-column desktop row layout below; mobile stacks each
            person's fields vertically instead (see the per-person block),
            with its own inline labels, so this row is skipped there. */}
        {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 85px 125px 105px 1fr 32px', gap: '10px', padding: '10px 22px 6px', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <span style={colHead}>#</span>
          <span style={colHead}>Full Name</span>
          <span style={colHead}>Gender</span>
          <span style={colHead}>Date of Birth</span>
          <span style={colHead}>Time of Birth</span>
          <span style={colHead}>Birth Place</span>
          <span />
        </div>
        )}

        {/* Body — each person is one row on desktop (7-column grid, matches
            the header above); on mobile that same grid is unreadably
            cramped, so each person becomes its own stacked card instead —
            one field per line, each with its own label, matching the
            "top to bottom" layout asked for specifically for phone. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 22px 16px' }}>
          {isMobile ? persons.map((p, idx) => (
            <div key={p.id} style={{
              display: 'flex', flexDirection: 'column', gap: '10px',
              padding: '14px', marginBottom: idx < persons.length - 1 ? '12px' : 0,
              background: '#f8fafc', borderRadius: '12px', border: '1px solid #ECEEF2',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: 6,
                    background: p.dob ? '#00A19D' : '#e2e8f0',
                    color: p.dob ? '#fff' : '#94a3b8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '11px', flexShrink: 0,
                  }}>{idx + 1}</div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Person {idx + 1}</span>
                </div>
                {persons.length > 1 && (
                  <button onClick={() => handleRemove(p.id)} title="Remove" style={{
                    width: 26, height: 26, background: 'transparent', border: 'none', borderRadius: '6px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1',
                  }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              <div>
                <span style={{ ...colHead, display: 'block', marginBottom: '4px' }}>Full Name</span>
                <input style={inp} placeholder="Enter name" value={p.name}
                  onChange={e => handleChange(p.id, 'name', e.target.value)} />
              </div>

              <div>
                <span style={{ ...colHead, display: 'block', marginBottom: '4px' }}>Gender</span>
                <ThemedSelect style={inp} value={p.gender || 'Male'} options={['Male', 'Female']}
                  onChange={v => handleChange(p.id, 'gender', v)} />
              </div>

              <div>
                <span style={{ ...colHead, display: 'block', marginBottom: '4px' }}>Date of Birth</span>
                <input type="date" style={inp} value={p.dob}
                  onChange={e => handleChange(p.id, 'dob', e.target.value)} />
              </div>

              <div>
                <span style={{ ...colHead, display: 'block', marginBottom: '4px' }}>Time of Birth</span>
                <input type="time" style={inp} value={p.tob || ''}
                  onChange={e => handleChange(p.id, 'tob', e.target.value)} />
              </div>

              <div>
                <span style={{ ...colHead, display: 'block', marginBottom: '4px' }}>Birth Place</span>
                <PlaceAutocomplete
                  style={inp}
                  placeholder="Type city, then pick"
                  value={p.place || ''}
                  geo={p.placeGeo}
                  onTextChange={text => { handleChange(p.id, 'place', text); handleChange(p.id, 'placeGeo', null); }}
                  onSelect={pl => { handleChange(p.id, 'place', pl.label); handleChange(p.id, 'placeGeo', toPlaceGeo(pl)); }} />
              </div>
            </div>
          )) : persons.map((p, idx) => (
            <div key={p.id} style={{
              display: 'grid', gridTemplateColumns: '32px 1fr 85px 125px 105px 1fr 32px', gap: '10px',
              padding: '8px 0', alignItems: 'center',
              borderBottom: idx < persons.length - 1 ? '1px solid #f1f5f9' : 'none'
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: p.dob ? '#00A19D' : '#e2e8f0',
                color: p.dob ? '#fff' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '12px', flexShrink: 0, transition: 'all 0.2s'
              }}>{idx + 1}</div>

              <input style={inp} placeholder="Enter name" value={p.name}
                onChange={e => handleChange(p.id, 'name', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = '#00A19D'}
                onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'} />

              <ThemedSelect style={inp} value={p.gender || 'Male'} options={['Male', 'Female']}
                onChange={v => handleChange(p.id, 'gender', v)} />

              <input type="date" style={inp} value={p.dob}
                onChange={e => handleChange(p.id, 'dob', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = '#00A19D'}
                onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'} />

              <input type="time" style={inp} value={p.tob || ''}
                onChange={e => handleChange(p.id, 'tob', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = '#00A19D'}
                onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'} />

              <PlaceAutocomplete
                style={inp}
                placeholder="Type city, then pick"
                value={p.place || ''}
                geo={p.placeGeo}
                onTextChange={text => { handleChange(p.id, 'place', text); handleChange(p.id, 'placeGeo', null); }}
                onSelect={pl => { handleChange(p.id, 'place', pl.label); handleChange(p.id, 'placeGeo', toPlaceGeo(pl)); }} />

              {persons.length > 1 ? (
                <button onClick={() => handleRemove(p.id)} title="Remove" style={{
                  width: 28, height: 28, background: 'transparent', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#cbd5e1', transition: 'all 0.15s'
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}>
                  <Trash2 size={14} />
                </button>
              ) : <span />}
            </div>
          ))}

          {/* Add person row */}
          <button onClick={handleAdd} style={{
            width: '100%', padding: '10px', marginTop: '8px',
            background: '#f0fdfc', border: '1.5px dashed #00A19D', borderRadius: '10px',
            color: '#00A19D', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            transition: 'all 0.15s'
          }}
            onMouseEnter={e => { e.currentTarget.style.background = '#ccfbf1'; e.currentTarget.style.borderColor = '#00897B'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f0fdfc'; e.currentTarget.style.borderColor = '#00A19D'; }}>
            <Plus size={15} /> Add Person
          </button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '10px', padding: '14px 22px', borderTop: '1px solid #e2e8f0', flexShrink: 0, background: '#f8fafc' }}>
          <button onClick={leave.requestClose} style={{
            flex: 1, padding: '11px', background: '#fff', border: '1.5px solid #e2e8f0',
            borderRadius: '10px', cursor: 'pointer', color: '#64748b', fontSize: '13px', fontWeight: 600,
            transition: 'all 0.15s'
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#cbd5e1'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
            Cancel
          </button>
          <button onClick={handleSave} style={{
            flex: 2, padding: '11px', background: 'linear-gradient(135deg, #00A19D, #00897B)',
            border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#fff',
            fontSize: '13.5px', fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,161,157,0.3)',
            transition: 'all 0.15s'
          }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,161,157,0.4)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,161,157,0.3)'}>
            Save ({persons.length} person{persons.length > 1 ? 's' : ''})
          </button>
        </div>
      </div>
      {leave.dialog}
    </div>
  );
}

const colHead = { fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' };
